import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import FAQ from '../FAQ/FAQ';
import {
    FeatureHero,
    FeatureCtaButton,
    FeatureSection,
    FeatureCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './resourcePages.css';

const ARROW_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
        <polyline points="12 5 19 12 12 19"/>
    </svg>
);

const TOPIC_ICONS = [
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="9" y1="13" x2="15" y2="13"/>
            <line x1="9" y1="17" x2="15" y2="17"/>
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
    )
];

function HelpCenterMain(){
    const { t, toLocalizedPath, locale } = useLocale();
    const p = (key) => t(`resourcePages.helpCenter.${key}`);

    const [loader, setLoader] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setLoader(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    if (!loader) {
        return <Loader/>;
    }

    const topics = [1, 2, 3, 4].map((num, idx) => ({
        icon: TOPIC_ICONS[idx],
        title: p(`topic${['Invoices', 'Estimates', 'Payments', 'Reports'][idx]}Title`),
        description: p(`topic${['Invoices', 'Estimates', 'Payments', 'Reports'][idx]}Description`)
    }));

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.helpCenter} locale={locale} />

            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={<FeatureCtaButton to={toLocalizedPath('faqs')}>{p('heroCta')}</FeatureCtaButton>}
            />

            <FeatureSection alt title={p('topicsSectionTitle')} description={p('topicsSectionDescription')}>
                <div className="rp-card-grid">
                    {topics.map((topic, idx) => (
                        <div className="rp-card" key={idx}>
                            <div className="rp-card-icon">{topic.icon}</div>
                            <h3 className="rp-card-title">{topic.title}</h3>
                            <p className="rp-card-description">{topic.description}</p>
                        </div>
                    ))}
                </div>
            </FeatureSection>

            {/* Reuse the full FAQ content directly, since Help Center and FAQs cover the same answers */}
            <FAQ/>

            <FeatureSection title={p('moreSectionTitle')} description={p('moreSectionDescription')}>
                <div className="rp-card-grid rp-card-grid-3">
                    <div className="rp-card">
                        <h3 className="rp-card-title">{p('faqsCardTitle')}</h3>
                        <p className="rp-card-description">{p('faqsCardDescription')}</p>
                        <Link to={toLocalizedPath('faqs')} className="rp-card-link">{p('faqsCardCta')} {ARROW_ICON}</Link>
                    </div>
                    <div className="rp-card">
                        <h3 className="rp-card-title">{p('guidesCardTitle')}</h3>
                        <p className="rp-card-description">{p('guidesCardDescription')}</p>
                        <Link to={toLocalizedPath('guides')} className="rp-card-link">{p('guidesCardCta')} {ARROW_ICON}</Link>
                    </div>
                    <div className="rp-card">
                        <h3 className="rp-card-title">{p('contactCardTitle')}</h3>
                        <p className="rp-card-description">{p('contactCardDescription')}</p>
                        <Link to={toLocalizedPath('contact')} className="rp-card-link">{p('contactCardCta')} {ARROW_ICON}</Link>
                    </div>
                </div>
            </FeatureSection>

            <FeatureCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaTo={toLocalizedPath('contact')}
                ctaLabel={p('ctaButton')}
            />

            <Footer/>
        </Fragment>
    );
}

export default HelpCenterMain;
